import { useEffect, useRef } from 'react';
import { useDirtyFormContext } from '../context/DirtyFormContext';

/**
 * Hook per segnalare al sistema che un form della pagina corrente è "dirty"
 * (ha modifiche non salvate).
 *
 * Uso tipico:
 * ```tsx
 * const isDirty = JSON.stringify(form) !== initialSnapshot;
 * useDirtyForm(isDirty, 'Scheda nominativo');
 * ```
 *
 * - L'ID univoco è generato automaticamente per ogni istanza del componente.
 * - Al unmount il form viene automaticamente rimosso dall'elenco dirty.
 * - Se `isDirty` diventa false, il form viene subito rimosso dall'elenco.
 *
 * @param isDirty true se il form ha modifiche non salvate
 * @param label etichetta human-readable mostrata in caso di warning (es. "Scheda nominativo")
 */
export function useDirtyForm(isDirty: boolean, label = 'Form non salvato') {
  const { registerDirty, clearDirty } = useDirtyFormContext();

  // ID univoco stabile per tutta la vita del componente
  const idRef = useRef<string>(
    `dirty-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  );

  useEffect(() => {
    const id = idRef.current;
    if (isDirty) {
      registerDirty(id, label);
    } else {
      clearDirty(id);
    }
    // Cleanup al unmount: rimuove sempre dall'elenco
    return () => clearDirty(id);
  }, [isDirty, label, registerDirty, clearDirty]);
}
